import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Clock, Edit, Plus, Minus, RotateCcw, History } from 'lucide-react'
import { getOrderModifications } from '@/services/orderService'
import { OrderItemModification } from '@/types/index'

interface OrderModificationHistoryProps {
  orderId: string
  trigger?: React.ReactNode
  onOpenChange?: (open: boolean) => void
}

const modificationTypeConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  added: { label: 'Item Added', icon: <Plus className="h-4 w-4" />, color: 'bg-green-100 text-green-800' },
  removed: { label: 'Item Removed', icon: <Minus className="h-4 w-4" />, color: 'bg-red-100 text-red-800' },
  restored: { label: 'Item Restored', icon: <RotateCcw className="h-4 w-4" />, color: 'bg-blue-100 text-blue-800' },
  quantity_changed: { label: 'Quantity Changed', icon: <Edit className="h-4 w-4" />, color: 'bg-yellow-100 text-yellow-800' },
  price_changed: { label: 'Price Changed', icon: <Edit className="h-4 w-4" />, color: 'bg-purple-100 text-purple-800' },
  notes_changed: { label: 'Notes Changed', icon: <Edit className="h-4 w-4" />, color: 'bg-gray-100 text-gray-800' }
}

// Define which modification types are considered "major changes"
const majorChangeTypes = ['added', 'removed', 'restored', 'quantity_changed', 'price_changed']

export function OrderModificationHistory({ orderId, trigger, onOpenChange }: OrderModificationHistoryProps) {
  const [modifications, setModifications] = useState<OrderItemModification[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
  }

  const loadModifications = async () => {
    setLoading(true)
    try {
      const result = await getOrderModifications(orderId)
      if (result.success && result.data) {
        // Debug: Log the first modification to see the data structure
        if (result.data.length > 0) {
          console.log('🔍 First modification data structure:', result.data[0])
        }
        
        // Filter to show only major changes
        const majorChanges = result.data.filter(modification => 
          majorChangeTypes.includes(modification.modification_type)
        )
        setModifications(majorChanges)
      }
    } catch (error) {
      console.error('Error loading modifications:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      loadModifications()
    }
  }, [open, orderId])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const renderModificationDetails = (modification: OrderItemModification) => {
    const config = modificationTypeConfig[modification.modification_type] || 
      { label: modification.modification_type, icon: <Edit className="h-4 w-4" />, color: 'bg-gray-100 text-gray-800' }

    // Get the item name from the related data
    const getItemName = (data: any) => {
      // Try different possible data structures
      if (data?.order_items?.menu_items?.name) {
        return data.order_items.menu_items.name
      }
      if (data?.menu_items?.name) {
        return data.menu_items.name
      }
      if (data?.name) {
        return data.name
      }
      // Check if we have the modification data directly
      if (modification.order_items?.menu_items?.name) {
        return modification.order_items.menu_items.name
      }
      
      // Try to get from old_value or new_value if they contain the item name
      if (modification.old_value && typeof modification.old_value === 'object') {
        const oldValue = modification.old_value as any
        if (oldValue.menu_items?.name) {
          return oldValue.menu_items.name
        }
        if (oldValue.name) {
          return oldValue.name
        }
      }
      
      if (modification.new_value && typeof modification.new_value === 'object') {
        const newValue = modification.new_value as any
        if (newValue.menu_items?.name) {
          return newValue.menu_items.name
        }
        if (newValue.name) {
          return newValue.name
        }
      }
      
      // Debug: Log what we're getting
      console.log('🔍 Data structure for item name:', {
        data,
        modification: modification.order_items,
        hasOrderItems: !!modification.order_items,
        hasMenuItems: !!modification.order_items?.menu_items,
        menuItemName: modification.order_items?.menu_items?.name,
        oldValue: modification.old_value,
        newValue: modification.new_value
      })
      
      return 'Unknown Item'
    }

    const renderItemDetails = (data: any, title: string, bgColor: string) => {
      if (!data) return null
      
      const itemName = getItemName(data)
      
      return (
        <div className="mb-3">
          <h5 className="text-xs font-semibold text-gray-500 mb-2">{title}:</h5>
          <div className={`text-sm ${bgColor} p-3 rounded border`}>
            {/* Item Name - Most Important */}
            <div className="mb-3 p-2 bg-white rounded border-l-4 border-blue-500">
              <div className="font-semibold text-blue-800">{itemName}</div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-xs">
              {data.quantity && (
                <div>
                  <span className="font-medium text-gray-600">Quantity:</span>
                  <span className="ml-1">{data.quantity}</span>
                </div>
              )}
              {data.unit_price && (
                <div>
                  <span className="font-medium text-gray-600">Unit Price:</span>
                  <span className="ml-1">${data.unit_price}</span>
                </div>
              )}
              {data.total_price && (
                <div>
                  <span className="font-medium text-gray-600">Total Price:</span>
                  <span className="ml-1">${data.total_price}</span>
                </div>
              )}
              {data.notes && (
                <div className="col-span-2">
                  <span className="font-medium text-gray-600">Notes:</span>
                  <span className="ml-1">{data.notes}</span>
                </div>
              )}
              {data.is_removed !== undefined && (
                <div>
                  <span className="font-medium text-gray-600">Status:</span>
                  <span className={`ml-1 ${data.is_removed ? 'text-red-600' : 'text-green-600'}`}>
                    {data.is_removed ? 'Removed' : 'Active'}
                  </span>
                </div>
              )}
              {data.removed_at && (
                <div>
                  <span className="font-medium text-gray-600">Removed At:</span>
                  <span className="ml-1">{formatDate(data.removed_at)}</span>
                </div>
              )}
              {data.acknowledgment_timestamp && (
                <div>
                  <span className="font-medium text-gray-600">Acknowledged:</span>
                  <span className="ml-1">{formatDate(data.acknowledgment_timestamp)}</span>
                </div>
              )}
              {data.modification_count && (
                <div>
                  <span className="font-medium text-gray-600">Modifications:</span>
                  <span className="ml-1">{data.modification_count}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }

    return (
      <Card key={modification.id} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${config.color}`}>
                {config.icon}
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">{config.label}</CardTitle>
                <p className="text-xs text-gray-500">{formatDate(modification.created_at)}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              {modification.modification_type.replace('_', ' ')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {modification.notes && (
            <p className="text-sm text-gray-600 mb-3">{modification.notes}</p>
          )}
          
          {renderItemDetails(modification.old_value, 'Previous Value', 'bg-gray-50')}
          {renderItemDetails(modification.new_value, 'New Value', 'bg-green-50')}
        </CardContent>
      </Card>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <History className="h-3 w-3 mr-1" />
            Modification History
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Order Modification History
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading modification history...</p>
              </div>
            </div>
          ) : modifications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold mb-2">No modifications found</h3>
              <p>This order hasn't been modified yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {modifications.map(renderModificationDetails)}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
} 